import { useState, useEffect, useCallback } from "react";
import { Button } from "reactstrap";
import {
  shouldShowCookieBanner,
  handleConsentAcceptance,
  CONSENT_TYPES
} from "../../../shared/utils/config/cookies";
import { VERIFY_AGE_SET } from "../../../shared/constants/common";
import { showError } from "../../../shared/utils/swalHandler";
import { enableWebPushFromUserGesture } from "../../../shared/pusNotification/fcm/enableWebPush";
import "./CookiesConsentBanner.css";

/**
 * CookiesConsentBanner Component
 * Displays a cookie consent banner at the bottom of the page
 * Allows users to accept all cookies or only necessary ones
 */
function CookiesConsentBanner() {
  const [showBanner, setShowBanner] = useState(false);

  const checkShouldShow = useCallback(() => {
    setShowBanner(shouldShowCookieBanner());
  }, []);

  useEffect(() => {
    checkShouldShow();

    const sync = () => checkShouldShow();

    window.addEventListener(VERIFY_AGE_SET, sync);
    window.addEventListener("storage", sync);

    return () => {
      window.removeEventListener(VERIFY_AGE_SET, sync);
      window.removeEventListener("storage", sync);
    };
  }, [checkShouldShow]);

  const handleAcceptAll = useCallback(async () => {
    const success = handleConsentAcceptance(CONSENT_TYPES.ALL);

    if (success) {
      // Safari only lists the site under Website Notifications after requestPermission()
      // runs from a user gesture — keep this in the same click handler as consent.
      try {
        await enableWebPushFromUserGesture();
      } catch (_) {
        // User dismissed or browser blocked the prompt — non-fatal.
      }

      if (!shouldShowCookieBanner()) {
        setShowBanner(false);
      }
    } else if (!success) {
      await showError("Error", "Failed to set cookie consent for all");
    }
  }, []);

  const handleNecessaryOnly = useCallback(async () => {
    const success = handleConsentAcceptance(CONSENT_TYPES.NECESSARY);

    if (success && !shouldShowCookieBanner()) {
      setShowBanner(false);
    } else if (!success) {
      await showError("Error", "Failed to set cookie consent for necessary");
    }
  }, []);

  if (!showBanner) return null;

  return (
    <div className="cookies-banner-bottom">
      <div className="cookies-content">
        <p className="text-white mb-2">
          We use cookies to run this site. Without them, some features won't
          work.{" "}
          See our{" "}
          <a
            href="https://juicyhouse.com/privacy#CookiesandAutomaticData"
            target="_blank"
            rel="noopener noreferrer"
            className="cookies-link"
          >
            Cookie Notice
          </a>
          .
        </p>
        <div className="cookies-buttons">
          <Button
            outline
            color="light"
            className="btn-round"
            onClick={handleNecessaryOnly}
          >
            Only Necessary
          </Button>
          <Button
            color="danger"
            className="btn-round"
            onClick={handleAcceptAll}
          >
            Accept All
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CookiesConsentBanner;
